import {
  responseGetSettlements,
  failureGetSettlements,
  requestGetSettlements,
  responseDownloadSettlementsPDF,
  requestDownloadSettlementsPDF,
  failureDownloadSettlementsPDF,
  requestSendSettlementsEmail,
  responseSendSettlementsEmail,
  failureSendSettlementsEmail,
  requestGenerateBulkStore,
  responseGenerateBulkStore,
  failureGenerateBulkStore,
  setInvoiceJobProgress,
} from 'reactRedux/slices/settlementsSlice/SettlementsSlice';
import requestApi from 'utils/Request';
import { downloadFile } from 'utils/DownloadRequest';
import {
  SETTLEMENTS,
  DOWNLOAD_INVOICE,
  DOWNLOAD_SELLER_INVOICE,
  SEND_INVOICE,
  INVOICE_JOB,
} from 'utils/StaticApiEndpoints';
import { showErrorAlert, showSuccessAlert } from 'utils/AlertUtils';
import { requestDownloadSettlementPDF } from 'reactRedux/slices/settlementsSlice/SettlementsSlice';
import { responseDownloadSettlementPDF } from 'reactRedux/slices/settlementsSlice/SettlementsSlice';
import { failureDownloadSettlementPDF } from 'reactRedux/slices/settlementsSlice/SettlementsSlice';
import { DOWNLOAD_SETTLEMENT } from 'utils/StaticApiEndpoints';

// ---- Async invoice job helpers (RAT-1579) ----
const INVOICE_JOB_POLL_INTERVAL_MS = 2500;
const INVOICE_JOB_MAX_POLL_ATTEMPTS = 240; // ~10 minutes safety cap

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Builds the enqueue body from the existing modal payload shape (merchantIds is
// a comma-separated string there).
const buildInvoiceJobBody = (operation, payload) => {
  const merchantIds =
    typeof payload?.merchantIds === 'string'
      ? payload.merchantIds
          .split(',')
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !Number.isNaN(id))
      : Array.isArray(payload?.merchantIds)
        ? payload.merchantIds
        : [];

  const body = { operation, merchantIds, year: parseInt(payload?.year, 10) };
  if (payload?.quarter) body.quarter = payload.quarter;
  if (payload?.month) body.month = payload.month;
  if (payload?.language) body.language = payload.language;
  return body;
};

// Enqueues a job and polls its status until completion. Resolves with the final
// status/result so the caller can download the file or surface an error.
const runInvoiceJob = async (dispatch, operation, payload) => {
  const enqueueResponse = await requestApi.post(
    INVOICE_JOB,
    buildInvoiceJobBody(operation, payload),
  );
  const queueId = enqueueResponse?.data?.queueId;
  if (!queueId) {
    throw new Error(enqueueResponse?.message || 'Failed to start invoice job');
  }

  for (let attempt = 0; attempt < INVOICE_JOB_MAX_POLL_ATTEMPTS; attempt += 1) {
    const statusResponse = await requestApi.get(`${INVOICE_JOB}/${queueId}`);
    const status = statusResponse?.data?.status;
    const result = statusResponse?.data?.result;

    if (result && typeof result.total === 'number') {
      dispatch(
        setInvoiceJobProgress({ done: result.done || 0, total: result.total }),
      );
    }

    if (status === 'completed') {
      return { queueId, status, result };
    }
    if (status === 'failed') {
      const message = result?.errors?.[0]?.message || 'Invoice job failed';
      throw new Error(message);
    }

    await sleep(INVOICE_JOB_POLL_INTERVAL_MS);
  }

  throw new Error('Invoice job timed out. Please check again shortly.');
};

export const GetSettelementsData = (payload, callback) => async (dispatch) => {
  try {
    dispatch(requestGetSettlements());
    const { data } = await requestApi.get(`${SETTLEMENTS}`, {
      params: payload,
    });
    dispatch(responseGetSettlements(data));
    if (typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    showErrorAlert(error?.message);
    dispatch(failureGetSettlements(error?.message));
  }
};

///////// Download Settlements PDF (Merchant Invoice) ////////
export const DownloadSettlementsPDF =
  (payload, callback) => async (dispatch) => {
    try {
      dispatch(requestDownloadSettlementsPDF());

      const defaultFilename = `INV-${payload.year}Y${payload?.quarter ? `Q${payload?.quarter}` : `M${payload?.month}`}-SP-${payload.name}`;

      await downloadFile(
        DOWNLOAD_INVOICE,
        payload,
        defaultFilename,
        'application/pdf',
        '.pdf',
        () => {
          dispatch(responseDownloadSettlementsPDF());
          if (typeof callback === 'function') {
            callback();
          }
        },
        (error) => {
          showErrorAlert(error?.message);
          dispatch(failureDownloadSettlementsPDF(error?.message));
        },
      );
    } catch (error) {
      showErrorAlert(error?.message);
      dispatch(failureDownloadSettlementsPDF(error?.message));
    }
  };

///////// Download Seller Invoice PDF ////////
export const DownloadSellerInvoicePDF =
  (payload, callback, onError) => async (dispatch) => {
    try {
      dispatch(requestDownloadSettlementsPDF());

      const defaultFilename = `INV-${payload.year}Y${payload?.quarter ? `Q${payload?.quarter}` : `M${payload?.month}`}-SP-${payload.name}`;

      await downloadFile(
        DOWNLOAD_SELLER_INVOICE,
        {
          sellerId: payload.sellerId,
          year: payload.year,
          ...(payload?.quarter
            ? { quarter: payload.quarter }
            : { month: payload.month }),
          ...(payload?.language &&
            payload.language !== 'en' && { language: payload.language }),
        },
        defaultFilename,
        'application/pdf',
        '.pdf',
        () => {
          dispatch(responseDownloadSettlementsPDF());
          if (typeof callback === 'function') {
            callback();
          }
        },
        (error) => {
          dispatch(failureDownloadSettlementsPDF(error?.message));
          if (typeof onError === 'function') {
            onError();
          }
          showErrorAlert(error?.message);
        },
      );
    } catch (error) {
      dispatch(failureDownloadSettlementsPDF(error?.message));
      if (typeof onError === 'function') {
        onError();
      }
      showErrorAlert(error?.message);
    }
  };

/////// Generate Bulk Store (async job - RAT-1579) ////////
export const GenerateBulkStore = (payload, callback) => async (dispatch) => {
  try {
    dispatch(requestGenerateBulkStore());
    await runInvoiceJob(dispatch, 'merchant_generate', payload);
    showSuccessAlert('Invoices generated and stored successfully!');
    dispatch(responseGenerateBulkStore());
    if (typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    showErrorAlert(
      error?.message || 'An error occurred while generating invoices.',
    );
    dispatch(failureGenerateBulkStore(error?.message));
  } finally {
    dispatch(setInvoiceJobProgress(null));
  }
};

///////// Send Settlements Email ////////
export const SendSettlementsEmail = (payload, callback) => async (dispatch) => {
  try {
    dispatch(requestSendSettlementsEmail());
    const response = await requestApi.get(`${SEND_INVOICE}`, {
      params: payload,
    });

    if (response?.statusCode === 200) {
      showSuccessAlert('Email sent successfully!');
      dispatch(responseSendSettlementsEmail());
      if (typeof callback === 'function') {
        callback();
      }
    } else {
      // Handle non-200 responses
      showErrorAlert('Failed to send email. Please try again.');
      dispatch(
        failureSendSettlementsEmail(
          response?.message || 'Failed to send email',
        ),
      );
    }
  } catch (error) {
    showErrorAlert('An error occurred while sending email.');
    dispatch(failureSendSettlementsEmail(error?.message));
  }
};

/////// Send Settlements Email Bulk (async job - RAT-1579) ////////
export const SendSettlementsEmailBulk =
  (payload, callback) => async (dispatch) => {
    try {
      dispatch(requestSendSettlementsEmail());
      await runInvoiceJob(dispatch, 'merchant_email', payload);
      showSuccessAlert('Emails sent successfully!');
      dispatch(responseSendSettlementsEmail());
      if (typeof callback === 'function') {
        callback();
      }
    } catch (error) {
      showErrorAlert(
        error?.message || 'An error occurred while sending emails.',
      );
      dispatch(failureSendSettlementsEmail(error?.message));
    } finally {
      dispatch(setInvoiceJobProgress(null));
    }
  };

////// Settlement Module download PDF ////////
export const DownloadSettlementPDF =
  (payload, callback) => async (dispatch) => {
    try {
      dispatch(requestDownloadSettlementPDF());

      const defaultFilename = 'P&L Statement';

      await downloadFile(
        DOWNLOAD_SETTLEMENT,
        payload,
        defaultFilename,
        'application/pdf',
        '.pdf',
        () => {
          dispatch(responseDownloadSettlementPDF());
          if (typeof callback === 'function') {
            callback();
          }
        },
        (error) => {
          showErrorAlert(error?.message);
          dispatch(failureDownloadSettlementPDF(error?.message));
        },
      );
    } catch (error) {
      showErrorAlert(error?.message);
      dispatch(failureDownloadSettlementPDF(error?.message));
    }
  };

/////// Download Settlements PDF Bulk (async job - RAT-1579) ////////
export const DownloadSettlementsPDFBulk =
  (payload, callback) => async (dispatch) => {
    try {
      dispatch(requestDownloadSettlementsPDF());

      const { queueId } = await runInvoiceJob(
        dispatch,
        'merchant_download',
        payload,
      );

      const idCount =
        typeof payload?.merchantIds === 'string'
          ? payload.merchantIds.split(',').filter((id) => id.trim()).length
          : Array.isArray(payload?.merchantIds)
            ? payload.merchantIds.length
            : 0;
      const isZip = idCount !== 1;
      const defaultFilename = `Bulk-INV-${payload.year}Y${payload?.quarter ? `Q${payload?.quarter}` : `M${payload?.month}`}`;

      await downloadFile(
        `${INVOICE_JOB}/${queueId}/download`,
        {},
        defaultFilename,
        isZip ? 'application/zip' : 'application/pdf',
        isZip ? '.zip' : '.pdf',
        () => {
          dispatch(responseDownloadSettlementsPDF());
          if (typeof callback === 'function') {
            callback();
          }
        },
        (error) => {
          showErrorAlert(error?.message);
          dispatch(failureDownloadSettlementsPDF(error?.message));
        },
      );
    } catch (error) {
      showErrorAlert(
        error?.message || 'An error occurred while downloading invoices.',
      );
      dispatch(failureDownloadSettlementsPDF(error?.message));
    } finally {
      dispatch(setInvoiceJobProgress(null));
    }
  };
